import React, { useEffect, useMemo, useState } from "react";
import { Search, X, Check, Minus, Plus } from "lucide-react";
import bookMedicineIcon from "../../assets/bookmedicineicon.svg";
import {
  formatMedicinePriceAmount,
  getMedicineDisplayPrice,
} from "./medicinePrice";
import { renderMedicineTypeIcon } from "./medicineTypeIcon";

const getTypeKey = (item) => {
  const type = (
    item?.drugCategory ||
    item?.medicineTypeOfSell ||
    ""
  ).toLowerCase();
  if (type.includes("capsule")) return "CAPSULES";
  if (type.includes("tablet")) return "TABLETS";
  if (
    type.includes("syrup") ||
    type.includes("suspension") ||
    type.includes("liquid")
  )
    return "SYRUP";
  return "OTHER";
};

const isUnavailable = (item) => {
  // Stock check disabled for now — allow adding all search results
  if (item?.inStock === false || item?.outOfStock === true) return true;
  if (item?.isInStock === false) return true;
  const stock = Number(item?.stock);
  if (!Number.isNaN(stock) && stock <= 0) return true;
  return false;
};

function AddMedicineModal({
  isOpen,
  onClose,
  patientName = "",
  appointmentId = "",
  existingCartDrugIds = new Set(),
  existingOrderDrugIds = new Set(),
  prescriptionDrugNames = [],
  onAddToOrder,
  isSubmitting = false,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // Figma: ek time par sirf ek medicine select — multi-select nahi
  const [selectedMedicine, setSelectedMedicine] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedMedicine(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const trimmed = searchQuery.trim();
    if (trimmed.length < 3) {
      setSearchResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_ES_URL}medicine?freeTextSearch=${encodeURIComponent(trimmed)}&pageNo=1&pageSize=20`,
        );
        const result = await response.json();
        setSearchResults(Array.isArray(result) ? result : []);
      } catch {
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, isOpen]);

  const prescriptionNamesLower = useMemo(
    () =>
      new Set(
        (prescriptionDrugNames || []).map((n) => (n || "").toLowerCase().trim()),
      ),
    [prescriptionDrugNames],
  );

  const groupedResults = useMemo(() => {
    const groups = {};
    searchResults.forEach((item) => {
      const key = getTypeKey(item);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    const order = ["CAPSULES", "TABLETS", "SYRUP", "OTHER"];
    return order
      .filter((k) => groups[k]?.length)
      .map((k) => ({ label: k, items: groups[k] }));
  }, [searchResults]);

  const totalQty = selectedMedicine?.quantity || 0;
  const totalPrice = selectedMedicine
    ? (getMedicineDisplayPrice(selectedMedicine.item) || 0) *
      selectedMedicine.quantity
    : 0;

  const isInPrescription = (item) => {
    const name = (item.medicineName || "").toLowerCase().trim();
    return prescriptionNamesLower.has(name);
  };

  const isDisabledItem = (item) => {
    const id = item.id;
    return (
      existingCartDrugIds.has(id) ||
      existingOrderDrugIds.has(id) ||
      isInPrescription(item)
    );
  };

  const selectMedicine = (item) => {
    if (isUnavailable(item) || isDisabledItem(item)) return;

    setSelectedMedicine((prev) => {
      if (prev?.item?.id === item.id) return null;
      return { item, quantity: 1 };
    });
  };

  const updateQty = (delta) => {
    setSelectedMedicine((prev) => {
      if (!prev) return prev;
      return { ...prev, quantity: Math.max(1, prev.quantity + delta) };
    });
  };

  const handleAddToOrder = () => {
    if (!selectedMedicine) return;
    onAddToOrder([selectedMedicine]);
  };

  if (!isOpen) return null;

  const resultCount = searchResults.length;
  const hasSearch = searchQuery.trim().length >= 3;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#004172] to-[#3cba84] px-5 py-4 text-white flex items-start justify-between gap-4 flex-shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <img src={bookMedicineIcon} alt="" className="w-6 h-6 object-contain" />
            </span>
            <div className="min-w-0">
              <h2 className="text-xl font-bold mb-0.5">Add Medicine</h2>
              <p className="text-white/85 text-sm mb-0 truncate">
                Search and select medicines for {patientName || "Patient"}
                {appointmentId ? ` · Appt #${appointmentId}` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg border border-white/40 flex items-center justify-center hover:bg-white/10 flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by medicine name, composition, or brand..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D6B6E]/30 focus:border-[#0D6B6E]"
              autoFocus
            />
          </div>

          {hasSearch && (
            <p className="text-xs text-gray-500 mb-0 mt-2">
              {loading
                ? "Searching..."
                : `${resultCount} result${resultCount === 1 ? "" : "s"} found for '${searchQuery.trim()}'`}
            </p>
          )}
          {!hasSearch && (
            <p className="text-xs text-gray-400 mb-0 mt-2">
              Type at least 3 characters to search medicines
            </p>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {!hasSearch && (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center text-gray-400">
              <Search className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm mb-0">Start typing to find medicines</p>
            </div>
          )}

          {hasSearch && !loading && resultCount === 0 && (
            <div className="py-12 text-center text-gray-400 text-sm">
              No medicines found. Try a different search term.
            </div>
          )}

          {groupedResults.map((group) => (
            <div key={group.label}>
              <div className="px-5 py-2 bg-gray-50 border-y border-gray-100">
                <span className="text-[11px] font-bold tracking-wide text-gray-500">
                  {group.label}
                </span>
              </div>
              {group.items.map((item) => {
                const id = item.id;
                const unavailable = isUnavailable(item);
                const disabled = isDisabledItem(item);
                const isSelected = selectedMedicine?.item?.id === id;
                const inCart = existingCartDrugIds.has(id);
                const inRx = isInPrescription(item);
                const displayPrice = formatMedicinePriceAmount(
                  getMedicineDisplayPrice(item),
                );
                const hasPrice = displayPrice != null;

                return (
                  <div
                    key={id}
                    role="button"
                    tabIndex={unavailable || disabled ? -1 : 0}
                    onClick={() => !unavailable && !disabled && selectMedicine(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectMedicine(item);
                      }
                    }}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-gray-50 transition-colors cursor-pointer ${
                      isSelected
                        ? "bg-[#E8F6F4] border-l-4 border-l-[#0D6B6E]"
                        : "border-l-4 border-l-transparent hover:bg-gray-50/80"
                    } ${unavailable || disabled ? "cursor-default opacity-80" : ""}`}
                  >
                    <input
                      type="radio"
                      name="medicine-select"
                      checked={isSelected}
                      disabled={unavailable || disabled}
                      onChange={() => selectMedicine(item)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 border-gray-300 text-[#0D6B6E] focus:ring-[#0D6B6E] flex-shrink-0 disabled:opacity-40"
                    />

                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "#F1F5F9" }}
                    >
                      {renderMedicineTypeIcon(item.medicineTypeOfSell)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 mb-0 text-sm truncate">
                          {item.medicineName}
                        </p>
                        {unavailable && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-red-50 text-red-600">
                            Out of stock
                          </span>
                        )}
                        {inCart && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-green-50 text-green-700">
                            In cart
                          </span>
                        )}
                        {inRx && !inCart && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-orange-50 text-orange-600">
                            In prescription
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-0 truncate">
                        {[item.composition || item.drugsInfoComposition, item.medicineTypeOfSell]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>

                    {isSelected && !unavailable && (
                      <div
                        className="flex items-center border border-gray-200 rounded-lg overflow-hidden flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => updateQty(-1)}
                          className="px-2 py-1 hover:bg-gray-50 text-gray-600"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="px-2 text-sm font-semibold min-w-[1.5rem] text-center">
                          {selectedMedicine.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQty(1)}
                          className="px-2 py-1 hover:bg-gray-50 text-gray-600"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}

                    {hasPrice && (
                      <span className="font-bold text-[#003A71] text-sm flex-shrink-0 whitespace-nowrap">
                        ₹{displayPrice}
                      </span>
                    )}

                    {unavailable ? (
                      <button
                        type="button"
                        disabled
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-400 flex-shrink-0"
                      >
                        Unavailable
                      </button>
                    ) : disabled ? (
                      <button
                        type="button"
                        disabled
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-500 flex-shrink-0"
                      >
                        Added
                      </button>
                    ) : isSelected ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectMedicine(item);
                        }}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 flex-shrink-0"
                      >
                        <Check className="w-3.5 h-3.5" />
                        Added
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectMedicine(item);
                        }}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#0D6B6E]/30 text-[#0D6B6E] hover:bg-[#0D6B6E]/5 flex-shrink-0"
                      >
                        + Add
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {loading && hasSearch && (
            <div className="py-12 text-center text-gray-500 text-sm">Loading medicines...</div>
          )}
        </div>

        {/* Selected chip (single) */}
        {selectedMedicine && (
          <div className="px-5 py-2 border-t border-gray-100 flex flex-wrap gap-2 flex-shrink-0 bg-gray-50/50">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-200 text-xs text-gray-700">
              {selectedMedicine.item.medicineName} · Qty: {selectedMedicine.quantity}
              <button
                type="button"
                onClick={() => setSelectedMedicine(null)}
                className="text-gray-400 hover:text-gray-700"
                aria-label={`Remove ${selectedMedicine.item.medicineName}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0 bg-white">
          <p className="text-sm text-gray-600 mb-0">
            {selectedMedicine ? (
              <>
                <span className="font-semibold text-gray-800">1 medicine selected</span>
                {totalPrice > 0 && (
                  <>
                    {" "}
                    · Total:{" "}
                    <span className="font-bold text-[#003A71]">
                      ₹{formatMedicinePriceAmount(totalPrice)}
                    </span>
                  </>
                )}
              </>
            ) : (
              "Select a medicine to add to the order"
            )}
          </p>
          <div className="flex items-center gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddToOrder}
              disabled={!selectedMedicine || isSubmitting}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#004172] to-[#3cba84] text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              + Add to Order
              {totalQty > 0 && (
                <span className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center text-xs font-bold">
                  {totalQty}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddMedicineModal;
